import { ChangeEvent, FC, useEffect } from 'react';

import type { CmsContentTypes } from '@eneco/cms-types/generated';
import { capitalizeFirstLetter } from '@eneco/common/string';
import { typedYupResolver } from '@eneco/hooks/form';
import { InputText, RadioButton, RadioGroup } from '@eneco/sparky';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Header, Layout, NextButton } from '../../components';
import { FIRST_NAME_MAX_LENGTH, SURNAME_MAX_LENGTH, SURNAME_PREPOSITION_MAX_LENGTH } from '../../helpers/constants';
import { useStepContent } from '../../hooks';
import { EnecoInHuisFlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';

interface FormFields {
  salutation: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
}

/**
 * The names the step is authored under: each lead flow keeps its own personal details step, and their
 * generated content types carry the same fields, apart from the salutation's name (see below).
 */
export type EnecoInHuisLeadFlowsPersonalDetailsContentName =
  | 'CvKetelKoopFlowPersonalDetailsStep'
  | 'HeatPumpLeadFlowPersonalDetailsStep'
  | 'SolarBoilerLeadFlowPersonalDetailsStep';

interface Props {
  /** The name the flow's personal details step is authored under. */
  contentName: EnecoInHuisLeadFlowsPersonalDetailsContentName;
}

/** The salutation question: the cv ketel koop flow calls it `salutationRadioList`, the other two `salutationList`. */
const salutationListOf = (content: CmsContentTypes[EnecoInHuisLeadFlowsPersonalDetailsContentName] | undefined) => {
  if (content && 'salutationRadioList' in content) return content.salutationRadioList;
  return content && 'salutationList' in content ? content.salutationList : undefined;
};

export const EnecoInHuisLeadFlowsPersonalDetailsStep: FC<Props> = ({ contentName }) => {
  const content = useStepContent(contentName);
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<EnecoInHuisFlowContext>();

  const sendToFlowMachine = useFlowActorRef().send;
  const salutation = useFlowSelector(state => state.context.salutation);
  const firstName = useFlowSelector(state => state.context.firstName);
  const surname = useFlowSelector(state => state.context.surname);
  const surnamePreposition = useFlowSelector(state => state.context.surnamePreposition);

  const salutationList = salutationListOf(content);

  const formSchema = yup.object({
    salutation: yup.string().required(salutationList?.requiredMessage),
    firstName: yup
      .string()
      .required(content?.firstNameFormField?.requiredMessage)
      .max(FIRST_NAME_MAX_LENGTH, content?.firstNameFormField?.validationMaxValueMessage),
    surnamePreposition: yup
      .string()
      .max(SURNAME_PREPOSITION_MAX_LENGTH, content?.surnamePrepositionFormField?.validationMaxValueMessage),
    surname: yup
      .string()
      .required(content?.surnameFormField?.requiredMessage)
      .max(SURNAME_MAX_LENGTH, content?.surnameFormField?.validationMaxValueMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onTouched',
    defaultValues: {
      salutation,
      firstName,
      surname,
      surnamePreposition,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          salutation: formData.salutation,
          firstName: formData.firstName,
          surnamePreposition: formData.surnamePreposition || undefined,
          surname: formData.surname,
        },
      });
    }
  }, [isSubmitSuccessful, sendToFlowMachine, getValues]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{content?.title}</Header.Title>
        {content?.text && <Header.Text>{content.text}</Header.Text>}
      </Header>
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            label={salutationList?.label ?? ''}
            name={name}
            direction="row"
            value={value}
            onValueChange={onChange}
            error={errors.salutation?.message}>
            {salutationList?.options?.map(option => (
              <RadioButton value={option.name ?? ''} key={option.name}>
                {option.label ?? ''}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />
      <InputText
        {...register('firstName', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('firstName', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={content?.firstNameFormField?.label ?? ''}
        placeholder={content?.firstNameFormField?.placeholder}
        hint={content?.firstNameFormField?.hint}
        error={errors.firstName?.message}
      />
      <InputText
        {...register('surnamePreposition')}
        label={content?.surnamePrepositionFormField?.label ?? ''}
        placeholder={content?.surnamePrepositionFormField?.placeholder}
        hint={content?.surnamePrepositionFormField?.hint}
        error={errors.surnamePreposition?.message}
      />

      <InputText
        {...register('surname', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={content?.surnameFormField?.label ?? ''}
        placeholder={content?.surnameFormField?.placeholder}
        hint={content?.surnameFormField?.hint}
        error={errors.surname?.message}
      />
      <NextButton isLoading={isSubmitSuccessful}>{content?.nextStepText}</NextButton>
    </Layout.Content>
  );
};
